import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useCreatePatient,
  useUpdatePatient,
  usePatient,
  useCheckDuplicate,
  useSearchPatients,
  useActiveClinicalAlerts,
  useActivePatientCategories,
  useSettings,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';
import { toast } from 'sonner';
import {
  ArrowLeft,
  AlertCircle,
  Loader2,
  ShieldAlert,
  CheckCircle,
  FileSearch,
  Search,
  UserCircle,
  Phone,
  Stethoscope,
  BedDouble,
  Calendar,
  X,
  Printer,
  Upload,
  ImageIcon,
  Trash2,
  User,
  MapPin,
  Home,
  IdCard,
  Siren,
  Shield,
  HeartPulse,
  UserPlus,
  ClipboardList,
  ToggleLeft,
  ToggleRight,
  ArrowUpRight,
} from 'lucide-react';
import { CreatePatientDto, Gender, BloodGroup, MaritalStatus, AgeGroup, Patient } from '../../types/patient';
import { useDivisions, useDistricts, useUpazilas, useUnions } from '../../services/httpServices/queries';
import { SearchableSelect, type Option as SearchableOption } from '../../components/ui/searchable-select';
import { OCCUPATION_OPTIONS } from '../../lib/occupations';
import { DateOfBirthInput } from '../../components/ui/date-of-birth-input';
import { DatePicker } from '../../components/ui/DatePicker';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { mediaService } from '../../services/httpServices/mediaService';
import type { PatientCategory as PatientCategoryRecord } from '../../services/httpServices/patientCategoryService';
import { buildPatientCardHtml, buildRegistrationSlipHtml } from '../../utils/patientPrint';
import { splitFullName, joinFullName } from '../../utils/patientName';
import { isLegacyLocationCode } from '../../utils/patientAddress';
import { openPrintWindow } from '../../utils/printWindow';
import { FormField, FormSection } from '../../components/ui/form-section';
import { useAppDispatch, useAppSelector } from '../../redux/store/hooks';
import {
  setPermField,
  setPresField,
  setSameAsPermanent,
  setResolvedAddressNames,
  resetAddressState,
  type BdAddressState,
} from '../../redux/features/bdAddressSlice';

const genderOptions: { value: Gender; label: string }[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const bloodGroupOptions: BloodGroup[] = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const maritalStatusOptions: { value: MaritalStatus; label: string }[] = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
  { value: 'separated', label: 'Separated' },
];

function determineAgeGroup(age: number): AgeGroup {
  if (age < 18) return 'pediatric';
  if (age >= 60) return 'senior';
  return 'adult';
}

/** Parse a `YYYY-MM-DD…` string as a *local* date (avoids the UTC shift of `new Date(iso)`). */
function parseIsoDateLocal(iso: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return new Date(NaN);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Age in whole months for a given date of birth (YYYY-MM-DD). */
function calculateAgeInMonths(dob: string): number {
  if (!dob) return 0;
  const today = new Date();
  const birth = parseIsoDateLocal(dob);
  let months =
    (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
  if (today.getDate() < birth.getDate()) months--;
  return Math.max(0, months);
}

/** Local `YYYY-MM-DD` for a Date, avoiding the UTC shift of `toISOString`. */
function toLocalIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Resolves the dataset id that drives the next cascading level. Stored values
 * are readable names; legacy records may still hold the upstream id.
 */
function findLocationId(
  rows: Array<{ id: string; name: string }> | undefined,
  value?: string | null,
): string | undefined {
  if (!value) return undefined;
  return (
    rows?.find((row) => row.name === value)?.id ??
    rows?.find((row) => row.id === value)?.id
  );
}

/** Address selects store the name, so options use the name as their value. */
function nameOptions(
  rows: Array<{ name: string }> | undefined,
): Array<{ value: string; label: string }> {
  return (rows || []).map((row) => ({ value: row.name, label: row.name }));
}

/**
 * Estimated date of birth for a patient who only knows their age. With no
 * months, only the birth year is known, so the date is anchored to Jan 1 of
 * that year (e.g. age 35 → 1991-01-01). With months, the date is counted back
 * from today and anchored to the 1st so the month reflects the entered age
 * (e.g. 1 year 2 months → the 1st, 14 months before today).
 */
function deriveEstimatedDob(years: number, months: number): string {
  if (months <= 0) return `${new Date().getFullYear() - years}-01-01`;
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - (years * 12 + months));
  return toLocalIsoDate(date);
}

type FieldErrors = Record<string, string | undefined>;

/**
 * Age is captured as whole years plus a leftover month count (0–11) so a mixed
 * age such as "1 year 2 months" can be recorded exactly. `ageMonths` is a
 * form-only field; the API still receives `age` in whole years.
 */
type RegistrationFormState = CreatePatientDto & { ageMonths?: number };

/** Optional detail groups on the registration form (collapsed by default). */
type OptionalSectionState = {
  emergencyContact: boolean;
  insurance: boolean;
  clinical: boolean;
};

function emptyForm(): RegistrationFormState {
  return {
    firstName: '',
    lastName: '',
    middleName: '',
    localName: '',
    gender: 'male',
    dateOfBirth: '',
    estimatedAge: false,
    age: undefined,
    ageMonths: undefined,
    ageGroup: undefined,
    bloodGroup: undefined,
    maritalStatus: undefined,
    nationality: 'Bangladeshi',
    mobileNumber: '',
    secondaryMobile: '',
    email: '',
    occupation: '',
    employer: '',
    nationalId: '',
    passport: '',
    birthCertificate: '',
    drivingLicense: '',
    insuranceCardNumber: '',
    guardianName: '',
    guardianRelation: '',
    guardianPhone: '',
    guardianAddress: '',
    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyContactPhone: '',
    emergencyContactAltPhone: '',
    emergencyContactAddress: '',
    insuranceCompany: '',
    policyNumber: '',
    corporateOrg: '',
    memberId: '',
    insuranceValidity: '',
    coveragePercent: undefined,
    authorizationNumber: '',
    drugAllergy: false,
    foodAllergy: false,
    pregnancy: false,
    highRisk: false,
    infectiousDisease: false,
    fallRisk: false,
    wheelchair: false,
    hearingImpairment: false,
    visualImpairment: false,
    photoUrl: '',
    medicalNote: '',
    patientCategory: 'general',
  };
}

/**
 * Header checkbox that opens an optional detail group. Unchecked groups are
 * collapsed and skipped, so only the relevant fields are shown per patient.
 */
function SectionToggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-1.5 text-[0.6875rem] font-medium text-text-muted">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
        checked={checked}
        onChange={onChange}
      />
      {checked ? 'Included' : 'Add'}
    </label>
  );
}

/** Placeholder shown in the body of a collapsed optional section. */
function CollapsedHint({ label }: { label: string }) {
  return <p className="text-[0.6875rem] text-text-muted">{label} not recorded — tick “Add” to include it.</p>;
}

interface PatientFormProps {
  mode?: 'create' | 'edit';
}

export function PatientForm({ mode = 'create' }: PatientFormProps) {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const isEdit = mode === 'edit';
  const patientId = routeId || '';
  const {
    data: editPatientData,
    isLoading: editPatientLoading,
    error: editPatientError,
  } = usePatient(isEdit ? patientId : '');
  const editPatient = editPatientData?.data;
  const updatePatient = useUpdatePatient();
  const bdAddress = useAppSelector((state) => state.bdAddress);

  // The address slice lives in Redux for the whole session, so a fresh
  // registration must explicitly clear the previous patient's address.
  useEffect(() => {
    if (!isEdit) dispatch(resetAddressState());
  }, [isEdit, dispatch]);

  const [form, setForm] = useState<RegistrationFormState>(emptyForm());
  // The form collects a single "Full Name"; first / middle / last are derived
  // from it so the API contract and the rest of the app stay unchanged.
  const [fullName, setFullName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [duplicatePatients, setDuplicatePatients] = useState<Patient[]>([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [registeredPatient, setRegisteredPatient] = useState<Patient | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isPhotoDragOver, setIsPhotoDragOver] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  // Secondary detail groups stay collapsed until the registrar opts in. This
  // keeps the default registration flow short for walk-in patients.
  const [optionalSections, setOptionalSections] = useState<OptionalSectionState>({
    emergencyContact: false,
    insurance: false,
    clinical: false,
  });

  const [identityEnabled, setIdentityEnabled] = useState({
    nationalId: false,
    passport: false,
    birthCertificate: false,
    drivingLicense: false,
    insuranceCard: false,
  });

  // Dynamic clinical alerts (managed via Patients > Clinical Alerts)
  const [selectedAlertIds, setSelectedAlertIds] = useState<string[]>([]);
  const { data: activeAlertsData } = useActiveClinicalAlerts();
  const dynamicAlerts = useMemo(() => (activeAlertsData?.data || []) as { id: string; name: string; label: string }[], [activeAlertsData]);

  // Dynamic patient categories — the single source of truth is Patients >
  // Categories (`/patients/categories`), so the dropdown always mirrors it.
  const { data: activeCategoriesData } = useActivePatientCategories();
  const activeCategories = useMemo(
    () => (activeCategoriesData?.data || []) as PatientCategoryRecord[],
    [activeCategoriesData],
  );
  const categoryOptions = useMemo(() => {
    const options = activeCategories.map((c) => ({
      value: c.name,
      label: c.label,
      description: c.description,
    }));
    // Ensure the currently selected value is always present as an option, even
    // when its category was renamed, deactivated or removed.
    if (form.patientCategory && !options.some((o) => o.value === form.patientCategory)) {
      options.unshift({ value: form.patientCategory, label: form.patientCategory, description: undefined });
    }
    return options;
  }, [activeCategories, form.patientCategory]);
  const selectedCategory = categoryOptions.find((c) => c.value === form.patientCategory);

  // Occupation: predefined list + any custom value the user typed earlier, so
  // an off-list occupation still renders as the selected label.
  const occupationOptions = useMemo<SearchableOption[]>(() => {
    const options: SearchableOption[] = OCCUPATION_OPTIONS.map(({ value, label, description, keywords }) => ({
      value,
      label,
      description,
      keywords,
    }));
    const current = form.occupation?.trim();
    if (current && !options.some((o) => o.value.toLowerCase() === current.toLowerCase())) {
      options.unshift({ value: current, label: current, description: 'Custom occupation' });
    }
    return options;
  }, [form.occupation]);

  const toggleOptionalSection = (key: keyof OptionalSectionState) => {
    setOptionalSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleClinicalAlert = (id: string) => {
    setSelectedAlertIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  };
  const debouncedSearch = useDebounce(searchQuery, 400);

  const createPatient = useCreatePatient();
  const { data: settingsData } = useSettings();

  const hospital = useMemo(() => {
    const find = (key: string) =>
      settingsData?.data?.find((s: any) => s.key === key)?.value?.trim() || '';
    return {
      name: find('hospital_name_en') || find('hospital_name') || 'Hospital',
      nameBn: find('hospital_name_bn'),
      logo: find('hospital_logo'),
      address: find('hospital_address'),
      email: find('hospital_email'),
      website: find('hospital_website'),
      hotline: find('hospital_hotline'),
      certified: find('hospital_certified'),
    };
  }, [settingsData]);

  const handlePrintDocument = (doc: 'slip' | 'card') => {
    if (!registeredPatient) return;
    // Registration slips and patient cards are regular documents, not thermal
    // receipts — they must not be sent to the receipt/label thermal printer.
    // Use the browser print dialog so the user can pick any printer (or save as
    // PDF). Browsers do not allow selecting a printer programmatically.
    const html =
      doc === 'card'
        ? buildPatientCardHtml(registeredPatient, hospital)
        : buildRegistrationSlipHtml(registeredPatient, hospital);

    if (!openPrintWindow(html)) {
      toast.error('Please allow pop-ups to print');
    }
  };

  // Closing the success modal (Done, X, or backdrop) finishes the registration
  // flow and returns to the patient list. The list query is invalidated on
  // create, so it refetches and shows the newly registered patient.
  const handleSuccessDone = () => {
    setShowSuccessModal(false);
    navigate('/patients');
  };

  const { data: searchData, isLoading: searchLoading } = useSearchPatients(debouncedSearch);
  const searchResults = (searchData?.data || []) as Patient[];
  const isSearching = debouncedSearch.length >= 2;

  // BD Location API queries. Address selects store the human-readable name on
  // the patient (that is what documents print); the dataset ids are derived
  // only to drive the cascading lookups.
  const { data: divisionsData, isLoading: divisionsLoading } = useDivisions();
  const divisionOptions = useMemo(() => nameOptions(divisionsData), [divisionsData]);
  const permDivisionId = useMemo(
    () => findLocationId(divisionsData, bdAddress.permDivision),
    [divisionsData, bdAddress.permDivision],
  );

  const { data: permDistrictsData, isLoading: permDistrictsLoading } = useDistricts(permDivisionId);
  const permDistrictOptions = useMemo(() => nameOptions(permDistrictsData), [permDistrictsData]);
  const permDistrictId = useMemo(
    () => findLocationId(permDistrictsData, bdAddress.permDistrict),
    [permDistrictsData, bdAddress.permDistrict],
  );

  const { data: permUpazilasData, isLoading: permUpazilasLoading } = useUpazilas(permDistrictId);
  const permUpazilaOptions = useMemo(() => nameOptions(permUpazilasData), [permUpazilasData]);
  const permUpazilaId = useMemo(
    () => findLocationId(permUpazilasData, bdAddress.permUpazila),
    [permUpazilasData, bdAddress.permUpazila],
  );

  const { data: permUnionsData, isLoading: permUnionsLoading } = useUnions(permUpazilaId);
  const permUnionOptions = useMemo(
    () => [
      { value: 'municipality', label: 'Municipality' },
      ...nameOptions(permUnionsData),
    ],
    [permUnionsData],
  );

  const presDivisionId = useMemo(
    () => findLocationId(divisionsData, bdAddress.presDivision),
    [divisionsData, bdAddress.presDivision],
  );
  const { data: presDistrictsData, isLoading: presDistrictsLoading } = useDistricts(presDivisionId);
  const presDistrictOptions = useMemo(() => nameOptions(presDistrictsData), [presDistrictsData]);
  const presDistrictId = useMemo(
    () => findLocationId(presDistrictsData, bdAddress.presDistrict),
    [presDistrictsData, bdAddress.presDistrict],
  );

  const { data: presUpazilasData, isLoading: presUpazilasLoading } = useUpazilas(presDistrictId);
  const presUpazilaOptions = useMemo(() => nameOptions(presUpazilasData), [presUpazilasData]);
  const presUpazilaId = useMemo(
    () => findLocationId(presUpazilasData, bdAddress.presUpazila),
    [presUpazilasData, bdAddress.presUpazila],
  );

  const { data: presUnionsData, isLoading: presUnionsLoading } = useUnions(presUpazilaId);
  const presUnionOptions = useMemo(
    () => [
      { value: 'municipality', label: 'Municipality' },
      ...nameOptions(presUnionsData),
    ],
    [presUnionsData],
  );

  /**
   * Records saved before the switch to names hold BD-API ids (`3`, `23`, ...).
   * As each cascading list loads, translate the stored id to its name so the
   * selects preselect correctly and the next save writes readable values.
   * A dedicated action is used because the normal setters clear children when
   * a parent changes — that would erase ids whose own list has not loaded yet.
   */
  useEffect(() => {
    const nameFor = (
      rows: Array<{ id: string; name: string }> | undefined,
      value?: string | null,
    ) => (isLegacyLocationCode(value) ? rows?.find((row) => row.id === value)?.name : undefined);

    const permUpdates: Partial<BdAddressState> = {};
    const permDivision = nameFor(divisionsData, bdAddress.permDivision);
    if (permDivision) permUpdates.permDivision = permDivision;
    const permDistrict = nameFor(permDistrictsData, bdAddress.permDistrict);
    if (permDistrict) permUpdates.permDistrict = permDistrict;
    const permUpazila = nameFor(permUpazilasData, bdAddress.permUpazila);
    if (permUpazila) permUpdates.permUpazila = permUpazila;
    const permUnion = nameFor(permUnionsData, bdAddress.permUnionCity);
    if (permUnion) permUpdates.permUnionCity = permUnion;
    if (Object.keys(permUpdates).length > 0) {
      dispatch(setResolvedAddressNames(permUpdates));
    }

    if (bdAddress.sameAsPermanent) return;

    const presUpdates: Partial<BdAddressState> = {};
    const presDivision = nameFor(divisionsData, bdAddress.presDivision);
    if (presDivision) presUpdates.presDivision = presDivision;
    const presDistrict = nameFor(presDistrictsData, bdAddress.presDistrict);
    if (presDistrict) presUpdates.presDistrict = presDistrict;
    const presUpazila = nameFor(presUpazilasData, bdAddress.presUpazila);
    if (presUpazila) presUpdates.presUpazila = presUpazila;
    const presUnion = nameFor(presUnionsData, bdAddress.presUnionCity);
    if (presUnion) presUpdates.presUnionCity = presUnion;
    if (Object.keys(presUpdates).length > 0) {
      dispatch(setResolvedAddressNames(presUpdates));
    }
  }, [
    divisionsData,
    permDistrictsData,
    permUpazilasData,
    permUnionsData,
    presDistrictsData,
    presUpazilasData,
    presUnionsData,
    bdAddress.permDivision,
    bdAddress.permDistrict,
    bdAddress.permUpazila,
    bdAddress.permUnionCity,
    bdAddress.presDivision,
    bdAddress.presDistrict,
    bdAddress.presUpazila,
    bdAddress.presUnionCity,
    bdAddress.sameAsPermanent,
    dispatch,
  ]);

  // Age is the source of truth when the date of birth is unknown; when a DOB
  // is present `update()` keeps these two in sync.
  const ageGroup = form.ageGroup ?? (form.age !== undefined ? determineAgeGroup(form.age) : undefined);

  const update = (field: string, value: any) => {
    if (field === 'sameAsPermanent') {
      dispatch(setSameAsPermanent(value));
      setErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
      return;
    }
    if (field === 'dateOfBirth') {
      // A date of birth always wins over the entered age; split it into whole
      // years plus the leftover months so an age like "1 year 2 months"
      // round-trips exactly.
      if (value) {
        const totalMonths = calculateAgeInMonths(value as string);
        const years = Math.floor(totalMonths / 12);
        const months = totalMonths % 12;
        setForm((prev) => ({
          ...prev,
          dateOfBirth: value,
          age: years,
          ageMonths: months || undefined,
          ageGroup: determineAgeGroup(years),
          estimatedAge: false,
        }));
      } else {
        setForm((prev) => ({ ...prev, dateOfBirth: '', estimatedAge: false }));
      }
      setErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
      return;
    }
    if (field === 'age') {
      const numeric =
        value === '' || value === null || value === undefined ? undefined : Number(value);
      setForm((prev) => {
        const next = { ...prev, age: numeric };
        if (numeric !== undefined && !Number.isNaN(numeric)) {
          next.ageGroup = determineAgeGroup(numeric);
          // Age drives the date of birth when the exact DOB is unknown.
          next.dateOfBirth = deriveEstimatedDob(numeric, prev.ageMonths ?? 0);
          next.estimatedAge = true;
        }
        return next;
      });
      setErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
      return;
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    // clear error for this field when user types
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  /**
   * Set the leftover months of the age (0–11). Combined with the whole years
   * entry this lets staff record a mixed age like 1 year 2 months.
   */
  const updateAgeMonths = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 2);
    const numeric = digits === '' ? undefined : Math.min(11, Number(digits));
    setForm((prev) => {
      const next = { ...prev, ageMonths: numeric };
      const years = prev.age ?? 0;
      if (years !== 0 || numeric !== undefined) {
        next.ageGroup = determineAgeGroup(years);
        next.dateOfBirth = deriveEstimatedDob(years, numeric ?? 0);
        next.estimatedAge = true;
      }
      return next;
    });
    setErrors((prev) => {
      if (!prev.age) return prev;
      const next = { ...prev };
      delete next.age;
      return next;
    });
  };

  const handleFullNameChange = (value: string) => {
    setFullName(value);
    // Keep the derived first / middle / last name fields in sync so the
    // duplicate check, payload builder and edit hydration all keep working.
    setForm((prev) => ({ ...prev, ...splitFullName(value) }));
    setErrors((prev) => {
      if (!prev.fullName) return prev;
      const next = { ...prev };
      delete next.fullName;
      return next;
    });
  };

  const updatePerm = (field: string, value: any) => {
    dispatch(setPermField({ field: field as any, value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const updatePres = (field: string, value: any) => {
    dispatch(setPresField({ field: field as any, value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const isBangladesh = (country?: string) =>
    !country || country.trim() === '' || country.trim().toLowerCase() === 'bangladesh';

  const toggleIdentity = (key: keyof typeof identityEnabled) => {
    setIdentityEnabled((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (!next[key]) {
        // clear field value when unchecked
        const fieldMap: Record<string, string> = {
          nationalId: 'nationalId',
          passport: 'passport',
          birthCertificate: 'birthCertificate',
          drivingLicense: 'drivingLicense',
          insuranceCard: 'insuranceCardNumber',
        };
        update(fieldMap[key], '');
      }
      return next;
    });
  };

  const validate = useCallback((): FieldErrors => {
    const e: FieldErrors = {};
    if (!fullName.trim()) e.fullName = 'Full name is required';
    const validYears =
      form.age !== undefined && !Number.isNaN(Number(form.age)) && Number(form.age) >= 0;
    const validMonths =
      form.ageMonths !== undefined &&
      !Number.isNaN(Number(form.ageMonths)) &&
      Number(form.ageMonths) >= 0;
    if (!validYears && !validMonths) e.age = 'Age is required';
    if (!form.mobileNumber.trim()) e.mobileNumber = 'Primary mobile number is required';
    setErrors(e);
    return e;
  }, [form, fullName]);

  // Only identity numbers are checked. The name (and date of birth) are not
  // unique — the same name with a different mobile number is a different person.
  const { refetch: refetchDuplicate } = useCheckDuplicate(
    {
      mobileNumber: form.mobileNumber,
      nationalId: form.nationalId,
      passport: form.passport,
    },
    false,
  );

  const buildPayload = (): CreatePatientDto => {
    // Legacy numeric ids are not printable names; drop any that could not be
    // resolved so they are never written back to the patient record.
    const locationName = (value?: string) =>
      value && !isLegacyLocationCode(value) ? value : undefined;
    const rest = form;
    // The API stores `age` in whole years. A child entered as "1 year 2 months"
    // is sent as 1 year; the precise DOB carries the month-level detail.
    const hasAge = rest.age !== undefined || rest.ageMonths !== undefined;
    const ageInYears = hasAge
      ? Math.floor(((rest.age ?? 0) * 12 + (rest.ageMonths ?? 0)) / 12)
      : undefined;
    const payload: CreatePatientDto = {
      ...rest,
      age: ageInYears,
      ageGroup: ageGroup,
      estimatedAge: rest.estimatedAge,
      dateOfBirth: rest.dateOfBirth || undefined,
      middleName: rest.middleName || undefined,
      localName: rest.localName || undefined,
      bloodGroup: rest.bloodGroup || undefined,
      maritalStatus: rest.maritalStatus || undefined,
      nationality: rest.nationality || undefined,
      secondaryMobile: rest.secondaryMobile || undefined,
      email: rest.email || undefined,
      occupation: rest.occupation || undefined,
      employer: rest.employer || undefined,
      // Address fields from Redux (single source of truth). Division/district/
      // upazila/union store names, never upstream dataset ids.
      permCountry: bdAddress.permCountry || undefined,
      permDivision: locationName(bdAddress.permDivision),
      permDistrict: locationName(bdAddress.permDistrict),
      permUpazila: locationName(bdAddress.permUpazila),
      permUnionCity: locationName(bdAddress.permUnionCity),
      permArea: bdAddress.permArea || undefined,
      permPostalCode: bdAddress.permPostalCode || undefined,
      permFullAddress: bdAddress.permFullAddress || undefined,
      sameAsPermanent: bdAddress.sameAsPermanent,
      presCountry: bdAddress.presCountry || undefined,
      presDivision: locationName(bdAddress.presDivision),
      presDistrict: locationName(bdAddress.presDistrict),
      presUpazila: locationName(bdAddress.presUpazila),
      presUnionCity: locationName(bdAddress.presUnionCity),
      presArea: bdAddress.presArea || undefined,
      presPostalCode: bdAddress.presPostalCode || undefined,
      presFullAddress: bdAddress.presFullAddress || undefined,
      nationalId: rest.nationalId || undefined,
      passport: rest.passport || undefined,
      birthCertificate: rest.birthCertificate || undefined,
      drivingLicense: rest.drivingLicense || undefined,
      insuranceCardNumber: rest.insuranceCardNumber || undefined,
      guardianName: rest.guardianName || undefined,
      guardianRelation: rest.guardianRelation || undefined,
      guardianPhone: rest.guardianPhone || undefined,
      guardianAddress: rest.guardianAddress || undefined,
      emergencyContactName: rest.emergencyContactName || undefined,
      emergencyContactRelation: rest.emergencyContactRelation || undefined,
      emergencyContactPhone: rest.emergencyContactPhone || undefined,
      emergencyContactAltPhone: rest.emergencyContactAltPhone || undefined,
      emergencyContactAddress: rest.emergencyContactAddress || undefined,
      insuranceCompany: rest.insuranceCompany || undefined,
      policyNumber: rest.policyNumber || undefined,
      corporateOrg: rest.corporateOrg || undefined,
      memberId: rest.memberId || undefined,
      insuranceValidity: rest.insuranceValidity || undefined,
      authorizationNumber: rest.authorizationNumber || undefined,
      photoUrl: rest.photoUrl || undefined,
      medicalNote: rest.medicalNote || undefined,
      clinicalAlertIds: selectedAlertIds.length > 0 ? selectedAlertIds : undefined,
    };
    // `ageMonths` is UI-only and the API DTO forbids unknown properties.
    delete (payload as { ageMonths?: number }).ageMonths;
    return payload;
  };

  const submitCreate = async (allowDuplicate = false) => {
    // Always validate the form here too: this runs directly from the
    // "Create Anyway" duplicate flow, which must not bypass validation.
    setErrors({});
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) return;

    try {
      setIsUploading(true);
      const payload: any = { ...buildPayload(), allowDuplicate };
      if (photoFile) {
        payload.photoUrl = (await mediaService.uploadImage(photoFile, 'patient')).data.url;
      }
      const patientRes = await createPatient.mutateAsync(payload);
      setRegisteredPatient(patientRes.data);
      setShowSuccessModal(true);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Something went wrong';
      setErrors({ general: message });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setDuplicatePatients([]);
    setShowDuplicateModal(false);
    setShowSuccessModal(false);
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) return;

    if (isEdit) {
      if (!patientId) return;
      try {
        setIsUploading(true);
        const payload: any = { ...buildPayload(), isActive };
        if (photoFile) {
          payload.photoUrl = (await mediaService.uploadImage(photoFile, 'patient')).data.url;
        }
        await updatePatient.mutateAsync({ id: patientId, data: payload });
        navigate(`/patients/${patientId}`);
      } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || 'Something went wrong';
        setErrors({ general: message });
      } finally {
        setIsUploading(false);
      }
      return;
    }

    try {
      const result = await refetchDuplicate();
      const duplicates = result.data?.data ?? [];
      if (duplicates.length > 0) {
        setDuplicatePatients(duplicates);
        setShowDuplicateModal(true);
        return;
      }
    } catch {
      // proceed if duplicate check fails
    }

    await submitCreate(false);
  };

  const handleDuplicateAction = (action: 'cancel' | 'proceed' | 'view') => {
    setShowDuplicateModal(false);
    if (action === 'cancel') {
      setDuplicatePatients([]);
      return;
    }
    if (action === 'view') {
      navigate('/patients');
      return;
    }
    if (action === 'proceed') {
      setDuplicatePatients([]);
      // Re-submit with an explicit confirmation so the API skips its
      // duplicate warning. Real uniqueness rules (e.g. mobile number)
      // are still enforced by the database.
      void submitCreate(true);
    }
  };

  const selectExistingPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setSearchQuery('');
  };

  // Up/Down moves through the smart-search matches, Enter selects the highlighted
  // patient. Only active while the results grid is visible.
  const patientNav = useListKeyboardNav({
    itemCount: searchResults.length,
    onSelect: (index) => {
      const patient = searchResults[index];
      if (patient) selectExistingPatient(patient);
    },
    resetKey: searchQuery,
    enabled: isSearching && !selectedPatient,
  });

  const clearSelectedPatient = () => {
    setSelectedPatient(null);
  };

  const inputBase =
    'w-full h-9 px-3 border rounded-lg bg-background text-text placeholder:text-text-muted/60 focus:outline-none focus:ring-2 text-sm transition-colors';
  const inputValid = 'border-border focus:ring-primary/20 focus:border-primary';
  const inputInvalid = 'border-danger focus:ring-danger/20 focus:border-danger';
  const labelClass =
    'mb-1.5 flex items-center gap-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-text-muted';
  const checkboxClass = 'h-4 w-4 rounded border-border text-primary focus:ring-primary';

  const ic = (field: string) => `${inputBase} ${errors[field] ? inputInvalid : inputValid}`;
  const err = (field: string) => errors[field] ? <p className="text-xs text-danger mt-1">{errors[field]}</p> : null;

  const handlePhotoDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPhotoDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handlePhotoFile(file);
    }
  }, []);

  const handlePhotoDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPhotoDragOver(true);
  }, []);

  const handlePhotoDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPhotoDragOver(false);
  }, []);

  const handlePhotoFile = (file: File) => {
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handlePhotoFile(file);
    }
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    update('photoUrl', '');
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  // ─── Edit mode: hydrate the form from the loaded patient ───
  useEffect(() => {
    if (!isEdit || !editPatient) return;
    const p = editPatient;
    const dob = p.dateOfBirth ? p.dateOfBirth.split('T')[0] : '';
    // A year-only age estimate is stored as Jan 1 of the birth year, so the
    // exact month cannot be recovered from the date — show the stored whole
    // years and no months. Otherwise (a real DOB, or an age that included
    // months) split the date into whole years + leftover months.
    const isYearOnlyEstimate = !!p.estimatedAge && dob.slice(5) === '01-01';
    const dobMonths = dob ? calculateAgeInMonths(dob) : 0;
    const dobYears = Math.floor(dobMonths / 12);
    const dobRemainder = dobMonths % 12;
    setForm({
      ...emptyForm(),
      firstName: p.firstName || '',
      middleName: p.middleName || '',
      lastName: p.lastName || '',
      localName: p.localName || '',
      gender: p.gender || 'male',
      dateOfBirth: dob,
      estimatedAge: p.estimatedAge ?? false,
      age: isYearOnlyEstimate ? p.age : dob ? dobYears : p.age,
      ageMonths: isYearOnlyEstimate ? undefined : dob ? dobRemainder || undefined : undefined,
      ageGroup:
        p.ageGroup ?? (dob ? determineAgeGroup(dobYears) : undefined),
      bloodGroup: p.bloodGroup,
      maritalStatus: p.maritalStatus,
      nationality: p.nationality || 'Bangladeshi',
      mobileNumber: p.mobileNumber || '',
      secondaryMobile: p.secondaryMobile || '',
      email: p.email || '',
      occupation: p.occupation || '',
      employer: p.employer || '',
      nationalId: p.nationalId || '',
      passport: p.passport || '',
      birthCertificate: p.birthCertificate || '',
      drivingLicense: p.drivingLicense || '',
      insuranceCardNumber: p.insuranceCardNumber || '',
      guardianName: p.guardianName || '',
      guardianRelation: p.guardianRelation || '',
      guardianPhone: p.guardianPhone || '',
      guardianAddress: p.guardianAddress || '',
      emergencyContactName: p.emergencyContactName || '',
      emergencyContactRelation: p.emergencyContactRelation || '',
      emergencyContactPhone: p.emergencyContactPhone || '',
      emergencyContactAltPhone: p.emergencyContactAltPhone || '',
      emergencyContactAddress: p.emergencyContactAddress || '',
      insuranceCompany: p.insuranceCompany || '',
      policyNumber: p.policyNumber || '',
      corporateOrg: p.corporateOrg || '',
      memberId: p.memberId || '',
      insuranceValidity: p.insuranceValidity ? p.insuranceValidity.split('T')[0] : '',
      coveragePercent: p.coveragePercent ?? undefined,
      authorizationNumber: p.authorizationNumber || '',
      drugAllergy: p.drugAllergy ?? false,
      foodAllergy: p.foodAllergy ?? false,
      pregnancy: p.pregnancy ?? false,
      highRisk: p.highRisk ?? false,
      infectiousDisease: p.infectiousDisease ?? false,
      fallRisk: p.fallRisk ?? false,
      wheelchair: p.wheelchair ?? false,
      hearingImpairment: p.hearingImpairment ?? false,
      visualImpairment: p.visualImpairment ?? false,
      photoUrl: p.photoUrl || '',
      medicalNote: p.medicalNote || '',
      patientCategory: p.patientCategory || 'general',
    });
    setFullName(joinFullName(p));
    setIsActive(p.isActive ?? true);
    setSelectedAlertIds((p.clinicalAlerts || []).map((a) => a.clinicalAlertId || a.id));
    // Expand the optional groups that already hold data so nothing is hidden
    // from the registrar while editing.
    setOptionalSections({
      emergencyContact: !!(
        p.emergencyContactName ||
        p.emergencyContactRelation ||
        p.emergencyContactPhone ||
        p.emergencyContactAltPhone ||
        p.emergencyContactAddress
      ),
      insurance: !!(
        p.insuranceCompany ||
        p.policyNumber ||
        p.corporateOrg ||
        p.memberId ||
        p.insuranceValidity ||
        p.coveragePercent ||
        p.authorizationNumber
      ),
      clinical: !!(p.medicalNote || (p.clinicalAlerts || []).length),
    });
    setIdentityEnabled({
      nationalId: !!p.nationalId,
      passport: !!p.passport,
      birthCertificate: !!p.birthCertificate,
      drivingLicense: !!p.drivingLicense,
      insuranceCard: !!p.insuranceCardNumber,
    });
    setPhotoPreview(p.photoUrl || null);

    // Hydrate the address slice (single source of truth for addresses)
    dispatch(setSameAsPermanent(!!p.sameAsPermanent));
    dispatch(setPermField({ field: 'permCountry', value: p.permCountry || 'Bangladesh' }));
    dispatch(setPermField({ field: 'permDivision', value: p.permDivision || '' }));
    dispatch(setPermField({ field: 'permDistrict', value: p.permDistrict || '' }));
    dispatch(setPermField({ field: 'permUpazila', value: p.permUpazila || '' }));
    dispatch(setPermField({ field: 'permUnionCity', value: p.permUnionCity || '' }));
    dispatch(setPermField({ field: 'permArea', value: p.permArea || '' }));
    dispatch(setPermField({ field: 'permPostalCode', value: p.permPostalCode || '' }));
    dispatch(setPermField({ field: 'permFullAddress', value: p.permFullAddress || '' }));
    if (!p.sameAsPermanent) {
      dispatch(setPresField({ field: 'presCountry', value: p.presCountry || 'Bangladesh' }));
      dispatch(setPresField({ field: 'presDivision', value: p.presDivision || '' }));
      dispatch(setPresField({ field: 'presDistrict', value: p.presDistrict || '' }));
      dispatch(setPresField({ field: 'presUpazila', value: p.presUpazila || '' }));
      dispatch(setPresField({ field: 'presUnionCity', value: p.presUnionCity || '' }));
      dispatch(setPresField({ field: 'presArea', value: p.presArea || '' }));
      dispatch(setPresField({ field: 'presPostalCode', value: p.presPostalCode || '' }));
      dispatch(setPresField({ field: 'presFullAddress', value: p.presFullAddress || '' }));
    }
  }, [isEdit, editPatient, dispatch]);

  const goBack = () => navigate(isEdit ? `/patients/${patientId}` : '/patients');

  if (isEdit && editPatientLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isEdit && (editPatientError || !editPatient)) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load patient. Please try again.</span>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 pb-2">
      {/* ─── Page header ─── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={goBack}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-surface text-text-muted transition-colors hover:border-primary/40 hover:text-primary"
            title={isEdit ? 'Back to Details' : 'Back to Patients'}
          >
            <ArrowLeft size={17} />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-xl font-bold leading-tight text-text">
                {isEdit ? 'Edit Patient' : 'Patient Registration'}
              </h1>
              <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-primary">
                {isEdit ? editPatient?.registrationNumber || 'ID' : 'ID auto'}
              </span>
            </div>
            <p className="truncate text-xs text-text-muted">
              {isEdit
                ? 'Update the patient profile, address and clinical records'
                : 'Register a new patient with contact, address and clinical details'}
            </p>
          </div>
        </div>
        {isEdit ? (
          <button
            type="button"
            onClick={() => setIsActive((prev) => !prev)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              isActive
                ? 'border-success/20 bg-success/10 text-success'
                : 'border-danger/20 bg-danger/10 text-danger'
            }`}
            title="Toggle patient status"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-success' : 'bg-danger'}`} />
            {isActive ? 'Active' : 'Inactive'}
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-info/20 bg-info/10 px-3 py-1 text-xs font-medium text-info">
            <UserPlus size={13} />
            New Registration
          </span>
        )}
      </div>

      {/* ─── Smart Patient Search (PRD Workflow Step 1) ─── */}
      {!isEdit && (
      <section className="rounded-2xl border border-border bg-surface shadow-sm">
        <div className="flex items-center gap-2.5 border-b border-border/70 px-4 py-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileSearch size={16} />
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold leading-tight text-text">Find Existing Patient</h2>
            <p className="mt-0.5 text-[0.6875rem] leading-snug text-text-muted">
              Search by name, mobile, UHID, national ID or passport before registering
            </p>
          </div>
        </div>
        <div className="space-y-3 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSelectedPatient(null); }}
            onKeyDown={patientNav.onKeyDown}
            placeholder="Search by name, mobile, UHID, national ID, or passport..."
            className="w-full h-9 pl-10 pr-4 border border-border rounded-lg bg-background text-text text-sm placeholder:text-text-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
          />
          {searchLoading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-text-muted" />
          )}
        </div>

        {isSearching && searchResults.length > 0 && !selectedPatient && (
          <div
            ref={patientNav.listRef}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2"
          >
            {searchResults.map((patient, index) => (
              <div
                key={patient.id}
                data-nav-index={index}
                onMouseEnter={() => patientNav.setHighlightedIndex(index)}
                className={`flex items-start gap-3 p-3 bg-background rounded-lg border transition-colors cursor-pointer ${
                  index === patientNav.highlightedIndex
                    ? 'border-primary ring-1 ring-primary/40'
                    : 'border-border hover:border-primary'
                }`}
                onClick={() => selectExistingPatient(patient)}
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <UserCircle size={16} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text truncate">{joinFullName(patient)}</p>
                  <p className="text-xs text-text-muted">{patient.registrationNumber}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-text-muted">
                    <span className="flex items-center gap-1"><Phone size={12} /> {patient.mobileNumber}</span>
                    <span className="flex items-center gap-1"><Calendar size={12} /> {patient.age ?? '-'} yrs</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {isSearching && searchResults.length === 0 && !searchLoading && (
          <p className="text-sm text-text-muted py-2">No patients found. You can register a new patient below.</p>
        )}

        {selectedPatient && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <UserCircle size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-text">{joinFullName(selectedPatient)}</p>
                  <p className="text-xs text-text-muted">{selectedPatient.registrationNumber} • {selectedPatient.mobileNumber}</p>
                </div>
              </div>
              <button onClick={clearSelectedPatient} className="text-text-muted hover:text-text">
                <X size={16} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/appointments')}
                className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium flex items-center gap-1 hover:bg-primary-dark transition-colors"
              >
                <Stethoscope size={14} />
                Book Appointment
              </button>
              <button
                onClick={() => navigate('/ipd')}
                className="px-3 py-1.5 bg-secondary text-white rounded-lg text-xs font-medium flex items-center gap-1 hover:bg-secondary-dark transition-colors"
              >
                <BedDouble size={14} />
                New IPD Admission
              </button>
              <button
                onClick={() => navigate('/patients')}
                className="px-3 py-1.5 bg-surface border border-border text-text rounded-lg text-xs font-medium flex items-center gap-1 hover:bg-background transition-colors"
              >
                <FileSearch size={14} />
                Open Profile
              </button>
            </div>
          </div>
        )}
        </div>
      </section>
      )}

      {/* General error */}
      {errors.general && (
        <div className="flex items-center gap-2 rounded-xl border border-danger/20 bg-danger/10 p-3 text-danger">
          <AlertCircle size={16} className="shrink-0" />
          <span className="text-xs font-medium">{errors.general}</span>
        </div>
      )}

      {/* Duplicate Warning Modal */}
      {showDuplicateModal && duplicatePatients.length > 0 && (
        <div className="space-y-3 rounded-2xl border border-warning/20 bg-warning/10 p-4">
          <div className="flex items-center gap-2 font-medium text-warning">
            <ShieldAlert size={18} />
            Possible duplicate patient(s) found
          </div>
          <div className="space-y-2">
            {duplicatePatients.map((dp) => (
              <div key={dp.id} className="flex items-center justify-between bg-background rounded-lg p-3 border border-border">
                <div>
                  <p className="text-sm font-medium text-text">{joinFullName(dp)}</p>
                  <p className="text-xs text-text-muted">ID: {dp.registrationNumber} | Mobile: {dp.mobileNumber}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-text-muted">
            Create Anyway is only for cases where these are genuinely different people.
            The primary mobile number must still be unique.
          </p>
          <div className="flex items-center gap-2 pt-1">
            <button onClick={() => handleDuplicateAction('cancel')} className="h-8 px-3 text-xs border border-border rounded-lg hover:bg-background transition-colors">
              Cancel
            </button>
            <button onClick={() => handleDuplicateAction('view')} className="h-8 px-3 text-xs bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-1">
              <FileSearch size={13} />
              Open Existing
            </button>
            <button onClick={() => handleDuplicateAction('proceed')} className="h-8 px-3 text-xs bg-warning text-white rounded-lg hover:bg-warning-dark transition-colors flex items-center gap-1">
              <CheckCircle size={13} />
              Create Anyway
            </button>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && registeredPatient && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="patient-success-title"
          onClick={handleSuccessDone}
        >
          <div
            className="bg-surface rounded-xl border border-border shadow-xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative p-6 text-center space-y-4">
              <button
                type="button"
                onClick={handleSuccessDone}
                className="absolute top-4 right-4 text-text-muted hover:text-text transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>

              <div className="mx-auto w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
                <CheckCircle size={28} className="text-success" />
              </div>

              <div className="space-y-1">
                <h3 id="patient-success-title" className="text-lg font-semibold text-text">
                  Patient Registered Successfully
                </h3>
                <p className="text-sm text-text-muted">
                  {joinFullName(registeredPatient)}
                </p>
                <p className="text-sm text-text-muted">
                  ID:{' '}
                  <span className="font-mono font-medium text-text">
                    {registeredPatient.registrationNumber}
                  </span>
                </p>
              </div>
            </div>

            <div className="px-6 pb-6 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handlePrintDocument('slip')}
                  className="px-4 py-2 bg-surface border border-border text-text rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-background transition-colors"
                >
                  <Printer size={16} />
                  Print Registration Slip
                </button>
                <button
                  type="button"
                  onClick={() => handlePrintDocument('card')}
                  className="px-4 py-2 bg-surface border border-border text-text rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-background transition-colors"
                >
                  <Printer size={16} />
                  Print Patient Card
                </button>
              </div>
              <button
                type="button"
                onClick={handleSuccessDone}
                className="w-full px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary-dark transition-colors"
              >
                <CheckCircle size={16} />
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <form
        id="patient-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_340px]"
      >
        {/* ═══ Main column ═══ */}
        <div className="min-w-0 space-y-4">
        {/* ─── Section 1: Basic Information ─── */}
        <FormSection
          icon={<User size={16} />}
          title="Basic Information"
          description="Patient identity and demographics"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="sm:col-span-2">
              <label className={labelClass}>Full Name <span className="text-danger">*</span></label>
              <input className={ic('fullName')} value={fullName} onChange={(e) => handleFullNameChange(e.target.value)} placeholder="Full name" />
              {err('fullName')}
            </div>
            <div>
              <label className={labelClass}>Gender <span className="text-danger">*</span></label>
              <select className={ic('gender')} value={form.gender} onChange={(e) => update('gender', e.target.value)}>
                {genderOptions.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
              {err('gender')}
            </div>
            <div>
              <label className={labelClass}>Husband / Father Name</label>
              <input className={ic('localName')} value={form.localName} onChange={(e) => update('localName', e.target.value)} placeholder="Husband or father's name" />
              {err('localName')}
            </div>
            <div>
              <label className={labelClass}>
                Date of Birth
                <span className="font-normal normal-case tracking-normal text-text-muted/70">(optional)</span>
              </label>
              <DateOfBirthInput className={ic('dateOfBirth')} value={form.dateOfBirth} onChange={(value) => update('dateOfBirth', value)} />
              {err('dateOfBirth')}
            </div>
            <div>
              <label className={`${labelClass} flex-wrap`}>
                Age <span className="text-danger">*</span>
                {(form.age !== undefined || form.ageMonths !== undefined) && ageGroup && (
                  <span className="ml-1 font-normal normal-case tracking-normal capitalize text-text-muted/70">
                    {ageGroup} · {form.estimatedAge ? 'DOB estimated from age' : 'derived from DOB'}
                  </span>
                )}
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  aria-label="Age years"
                  title="Years"
                  className={`min-w-0 h-9 flex-1 px-2 border rounded-lg bg-background text-text placeholder:text-text-muted/60 focus:outline-none focus:ring-2 text-sm transition-colors ${errors.age ? inputInvalid : inputValid}`}
                  value={form.age ?? ''}
                  onChange={(e) => update('age', e.target.value.replace(/\D/g, ''))}
                  placeholder="Year"
                />
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  aria-label="Age months"
                  title="Months"
                  maxLength={2}
                  className={`min-w-0 h-9 w-20 px-2 border rounded-lg bg-background text-text placeholder:text-text-muted/60 focus:outline-none focus:ring-2 text-sm transition-colors ${errors.age ? inputInvalid : inputValid}`}
                  value={form.ageMonths ?? ''}
                  onChange={(e) => updateAgeMonths(e.target.value)}
                  placeholder="Month"
                />
              </div>
              {err('age')}
            </div>
            <div>
              <label className={labelClass}>Blood Group</label>
              <select className={ic('bloodGroup')} value={form.bloodGroup || ''} onChange={(e) => update('bloodGroup', e.target.value || undefined)}>
                <option value="">Select</option>
                {bloodGroupOptions.map((bg) => <option key={bg} value={bg}>{bg}</option>)}
              </select>
              {err('bloodGroup')}
            </div>
            <div>
              <label className={labelClass}>Marital Status</label>
              <select className={ic('maritalStatus')} value={form.maritalStatus || ''} onChange={(e) => update('maritalStatus', e.target.value || undefined)}>
                <option value="">Select</option>
                {maritalStatusOptions.map((ms) => <option key={ms.value} value={ms.value}>{ms.label}</option>)}
              </select>
              {err('maritalStatus')}
            </div>
            <div>
              <label className={labelClass}>Nationality</label>
              <input className={ic('nationality')} value={form.nationality} onChange={(e) => update('nationality', e.target.value)} placeholder="e.g. Bangladeshi" />
              {err('nationality')}
            </div>
          </div>
        </FormSection>

        {/* ─── Section 2: Contact Information ─── */}
        <FormSection
          icon={<Phone size={16} />}
          title="Contact Information"
          description="How to reach the patient"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className={labelClass}>Primary Mobile *</label>
              <input className={ic('mobileNumber')} value={form.mobileNumber} onChange={(e) => update('mobileNumber', e.target.value)} placeholder="+8801XXXXXXXXX" />
              {err('mobileNumber')}
            </div>
            <div>
              <label className={labelClass}>Secondary Mobile</label>
              <input className={ic('secondaryMobile')} value={form.secondaryMobile} onChange={(e) => update('secondaryMobile', e.target.value)} placeholder="+8801XXXXXXXXX" />
              {err('secondaryMobile')}
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input type="email" className={ic('email')} value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="email@example.com" />
              {err('email')}
            </div>
            <div>
              <label className={labelClass}>Occupation</label>
              <SearchableSelect
                value={form.occupation || ''}
                onChange={(v) => update('occupation', v)}
                options={occupationOptions}
                placeholder="Search or type occupation"
                inputClassName="h-9 py-1.5"
                error={err('occupation')}
                onAddNew={(query) => update('occupation', query.trim())}
                hideAddNewWhenEmpty
                addNewLabel={(query) => `Use "${query.trim()}"`}
              />
            </div>
            <div>
              <label className={labelClass}>Employer</label>
              <input className={ic('employer')} value={form.employer} onChange={(e) => update('employer', e.target.value)} placeholder="Employer / Organization" />
              {err('employer')}
            </div>
          </div>
        </FormSection>

        {/* ─── Section 3: Permanent Address ─── */}
        <FormSection
          icon={<MapPin size={16} />}
          title="Permanent Address"
          description="Country, division and local address"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className={labelClass}>Country</label>
              <input className={ic('permCountry')} value={bdAddress.permCountry} onChange={(e) => updatePerm('permCountry', e.target.value)} />
              {err('permCountry')}
            </div>
            {isBangladesh(bdAddress.permCountry) ? (
              <>
                <SearchableSelect
                  label="Division"
                  labelClassName={labelClass}
                  value={bdAddress.permDivision || ''}
                  onChange={(v) => updatePerm('permDivision', v)}
                  options={divisionOptions}
                  placeholder="Select Division"
                  loading={divisionsLoading}
                  error={err('permDivision')}
                />
                <SearchableSelect
                  label="District"
                  labelClassName={labelClass}
                  value={bdAddress.permDistrict || ''}
                  onChange={(v) => updatePerm('permDistrict', v)}
                  options={permDistrictOptions}
                  placeholder="Select District"
                  loading={permDistrictsLoading}
                  disabled={!bdAddress.permDivision}
                  error={err('permDistrict')}
                />
                <SearchableSelect
                  label="Upazila"
                  labelClassName={labelClass}
                  value={bdAddress.permUpazila || ''}
                  onChange={(v) => updatePerm('permUpazila', v)}
                  options={permUpazilaOptions}
                  placeholder="Select Upazila"
                  loading={permUpazilasLoading}
                  disabled={!bdAddress.permDistrict}
                  error={err('permUpazila')}
                />
                <SearchableSelect
                  label="Union"
                  labelClassName={labelClass}
                  value={bdAddress.permUnionCity || ''}
                  onChange={(v) => updatePerm('permUnionCity', v)}
                  options={permUnionOptions}
                  placeholder="Select Union"
                  loading={permUnionsLoading}
                  disabled={!bdAddress.permUpazila}
                  error={err('permUnionCity')}
                />
              </>
            ) : (
              <>
                <div>
                  <label className={labelClass}>Division / State</label>
                  <input className={ic('permDivision')} value={bdAddress.permDivision} onChange={(e) => updatePerm('permDivision', e.target.value)} placeholder="Division" />
                  {err('permDivision')}
                </div>
                <div>
                  <label className={labelClass}>District</label>
                  <input className={ic('permDistrict')} value={bdAddress.permDistrict} onChange={(e) => updatePerm('permDistrict', e.target.value)} placeholder="District" />
                  {err('permDistrict')}
                </div>
                <div>
                  <label className={labelClass}>Upazila / City</label>
                  <input className={ic('permUpazila')} value={bdAddress.permUpazila} onChange={(e) => updatePerm('permUpazila', e.target.value)} placeholder="Upazila or City" />
                  {err('permUpazila')}
                </div>
                <div>
                  <label className={labelClass}>Union / City</label>
                  <input className={ic('permUnionCity')} value={bdAddress.permUnionCity} onChange={(e) => updatePerm('permUnionCity', e.target.value)} placeholder="Union or City" />
                  {err('permUnionCity')}
                </div>
              </>
            )}
            <div>
              <label className={labelClass}>Area</label>
              <input className={ic('permArea')} value={bdAddress.permArea} onChange={(e) => updatePerm('permArea', e.target.value)} placeholder="Area / Village" />
              {err('permArea')}
            </div>
            <div>
              <label className={labelClass}>Postal Code</label>
              <input className={ic('permPostalCode')} value={bdAddress.permPostalCode} onChange={(e) => updatePerm('permPostalCode', e.target.value)} placeholder="Postal code" />
              {err('permPostalCode')}
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Full Address</label>
              <input className={ic('permFullAddress')} value={bdAddress.permFullAddress} onChange={(e) => updatePerm('permFullAddress', e.target.value)} placeholder="House, Road, Village details" />
              {err('permFullAddress')}
            </div>
          </div>
        </FormSection>

        {/* ─── Section 4: Present Address ─── */}
        <FormSection
          icon={<Home size={16} />}
          title="Present Address"
          description="Where the patient currently lives"
          action={
            <label className="flex cursor-pointer items-center gap-1.5 text-[0.6875rem] font-medium text-text-muted">
              <input
                type="checkbox"
                className={checkboxClass}
                checked={bdAddress.sameAsPermanent}
                onChange={(e) => update('sameAsPermanent', e.target.checked)}
              />
              Same as permanent
            </label>
          }
        >
          {bdAddress.sameAsPermanent ? (
            <p className="text-[0.6875rem] text-text-muted">Same as the permanent address.</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div><label className={labelClass}>Country</label><input className={ic('presCountry')} value={bdAddress.presCountry} onChange={(e) => updatePres('presCountry', e.target.value)} />{err('presCountry')}</div>
              {isBangladesh(bdAddress.presCountry) ? (
                <>
                  <SearchableSelect
                    label="Division"
                    labelClassName={labelClass}
                    value={bdAddress.presDivision || ''}
                    onChange={(v) => updatePres('presDivision', v)}
                    options={divisionOptions}
                    placeholder="Select Division"
                    loading={divisionsLoading}
                    error={err('presDivision')}
                  />
                  <SearchableSelect
                    label="District"
                    labelClassName={labelClass}
                    value={bdAddress.presDistrict || ''}
                    onChange={(v) => updatePres('presDistrict', v)}
                    options={presDistrictOptions}
                    placeholder="Select District"
                    loading={presDistrictsLoading}
                    disabled={!bdAddress.presDivision}
                    error={err('presDistrict')}
                  />
                  <SearchableSelect
                    label="Upazila"
                    labelClassName={labelClass}
                    value={bdAddress.presUpazila || ''}
                    onChange={(v) => updatePres('presUpazila', v)}
                    options={presUpazilaOptions}
                    placeholder="Select Upazila"
                    loading={presUpazilasLoading}
                    disabled={!bdAddress.presDistrict}
                    error={err('presUpazila')}
                  />
                  <SearchableSelect
                    label="Union"
                    labelClassName={labelClass}
                    value={bdAddress.presUnionCity || ''}
                    onChange={(v) => updatePres('presUnionCity', v)}
                    options={presUnionOptions}
                    placeholder="Select Union"
                    loading={presUnionsLoading}
                    disabled={!bdAddress.presUpazila}
                    error={err('presUnionCity')}
                  />
                </>
              ) : (
                <>
                  <div><label className={labelClass}>Division / State</label><input className={ic('presDivision')} value={bdAddress.presDivision} onChange={(e) => updatePres('presDivision', e.target.value)} placeholder="Division" />{err('presDivision')}</div>
                  <div><label className={labelClass}>District</label><input className={ic('presDistrict')} value={bdAddress.presDistrict} onChange={(e) => updatePres('presDistrict', e.target.value)} placeholder="District" />{err('presDistrict')}</div>
                  <div><label className={labelClass}>Upazila / City</label><input className={ic('presUpazila')} value={bdAddress.presUpazila} onChange={(e) => updatePres('presUpazila', e.target.value)} placeholder="Upazila or City" />{err('presUpazila')}</div>
                  <div><label className={labelClass}>Union / City</label><input className={ic('presUnionCity')} value={bdAddress.presUnionCity} onChange={(e) => updatePres('presUnionCity', e.target.value)} placeholder="Union or City" />{err('presUnionCity')}</div>
                </>
              )}
              <div><label className={labelClass}>Area</label><input className={ic('presArea')} value={bdAddress.presArea} onChange={(e) => updatePres('presArea', e.target.value)} placeholder="Area / Village" />{err('presArea')}</div>
              <div><label className={labelClass}>Postal Code</label><input className={ic('presPostalCode')} value={bdAddress.presPostalCode} onChange={(e) => updatePres('presPostalCode', e.target.value)} placeholder="Postal code" />{err('presPostalCode')}</div>
              <div className="sm:col-span-2"><label className={labelClass}>Full Address</label><input className={ic('presFullAddress')} value={bdAddress.presFullAddress} onChange={(e) => updatePres('presFullAddress', e.target.value)} placeholder="House, Road, Village details" />{err('presFullAddress')}</div>
            </div>
          )}
        </FormSection>

        {/* ─── Section 5: Identity Information ─── */}
        <FormSection
          icon={<IdCard size={16} />}
          title="Identity Information"
          description="Optional documents — tick to add a number"
        >
          <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
            {[
              { key: 'nationalId' as const, label: 'National ID' },
              { key: 'passport' as const, label: 'Passport' },
              { key: 'birthCertificate' as const, label: 'Birth Certificate' },
              { key: 'drivingLicense' as const, label: 'Driving License' },
              { key: 'insuranceCard' as const, label: 'Insurance Card' },
            ].map((item) => (
              <label
                key={item.key}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[0.6875rem] font-medium transition-colors ${
                  identityEnabled[item.key]
                    ? 'border-primary/40 bg-primary/5 text-primary'
                    : 'border-border bg-background/60 text-text-muted hover:border-primary/30'
                }`}
              >
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={identityEnabled[item.key]}
                  onChange={() => toggleIdentity(item.key)}
                />
                {item.label}
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {identityEnabled.nationalId && (
              <div>
                <label className={labelClass}>National ID</label>
                <input className={ic('nationalId')} value={form.nationalId} onChange={(e) => update('nationalId', e.target.value)} placeholder="National ID number" />
                {err('nationalId')}
              </div>
            )}
            {identityEnabled.passport && (
              <div>
                <label className={labelClass}>Passport</label>
                <input className={ic('passport')} value={form.passport} onChange={(e) => update('passport', e.target.value)} placeholder="Passport number" />
                {err('passport')}
              </div>
            )}
            {identityEnabled.birthCertificate && (
              <div>
                <label className={labelClass}>Birth Certificate</label>
                <input className={ic('birthCertificate')} value={form.birthCertificate} onChange={(e) => update('birthCertificate', e.target.value)} placeholder="Birth certificate number" />
                {err('birthCertificate')}
              </div>
            )}
            {identityEnabled.drivingLicense && (
              <div>
                <label className={labelClass}>Driving License</label>
                <input className={ic('drivingLicense')} value={form.drivingLicense} onChange={(e) => update('drivingLicense', e.target.value)} placeholder="License number" />
                {err('drivingLicense')}
              </div>
            )}
            {identityEnabled.insuranceCard && (
              <div>
                <label className={labelClass}>Insurance Card</label>
                <input className={ic('insuranceCardNumber')} value={form.insuranceCardNumber} onChange={(e) => update('insuranceCardNumber', e.target.value)} placeholder="Insurance card number" />
                {err('insuranceCardNumber')}
              </div>
            )}
          </div>
        </FormSection>

        {/* ─── Section 6: Guardian Information ─── */}
        <FormSection
          icon={<User size={16} />}
          title="Guardian Information"
          description="Parent or guardian details"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><label className={labelClass}>Guardian Name</label><input className={ic('guardianName')} value={form.guardianName} onChange={(e) => update('guardianName', e.target.value)} placeholder="Guardian full name" />{err('guardianName')}</div>
            <div><label className={labelClass}>Relationship</label><input className={ic('guardianRelation')} value={form.guardianRelation} onChange={(e) => update('guardianRelation', e.target.value)} placeholder="e.g. Father, Mother" />{err('guardianRelation')}</div>
            <div><label className={labelClass}>Phone</label><input className={ic('guardianPhone')} value={form.guardianPhone} onChange={(e) => update('guardianPhone', e.target.value)} placeholder="+8801XXXXXXXXX" />{err('guardianPhone')}</div>
            <div><label className={labelClass}>Address</label><input className={ic('guardianAddress')} value={form.guardianAddress} onChange={(e) => update('guardianAddress', e.target.value)} placeholder="Guardian address" />{err('guardianAddress')}</div>
          </div>
        </FormSection>

        {/* ─── Section 7: Emergency Contact (optional) ─── */}
        <FormSection
          icon={<Siren size={16} />}
          title="Emergency Contact"
          description="Who to call in an emergency"
          action={<SectionToggle checked={optionalSections.emergencyContact} onChange={() => toggleOptionalSection('emergencyContact')} />}
        >
          {optionalSections.emergencyContact ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div><label className={labelClass}>Name</label><input className={ic('emergencyContactName')} value={form.emergencyContactName} onChange={(e) => update('emergencyContactName', e.target.value)} placeholder="Contact name" />{err('emergencyContactName')}</div>
            <div><label className={labelClass}>Relationship</label><input className={ic('emergencyContactRelation')} value={form.emergencyContactRelation} onChange={(e) => update('emergencyContactRelation', e.target.value)} placeholder="e.g. Spouse, Sibling" />{err('emergencyContactRelation')}</div>
            <div><label className={labelClass}>Phone</label><input className={ic('emergencyContactPhone')} value={form.emergencyContactPhone} onChange={(e) => update('emergencyContactPhone', e.target.value)} placeholder="+8801XXXXXXXXX" />{err('emergencyContactPhone')}</div>
            <div><label className={labelClass}>Alternative Phone</label><input className={ic('emergencyContactAltPhone')} value={form.emergencyContactAltPhone} onChange={(e) => update('emergencyContactAltPhone', e.target.value)} placeholder="+8801XXXXXXXXX" />{err('emergencyContactAltPhone')}</div>
            <div className="sm:col-span-2"><label className={labelClass}>Address</label><input className={ic('emergencyContactAddress')} value={form.emergencyContactAddress} onChange={(e) => update('emergencyContactAddress', e.target.value)} placeholder="Emergency contact address" />{err('emergencyContactAddress')}</div>
          </div>
          ) : (
            <CollapsedHint label="Emergency contact" />
          )}
        </FormSection>

        {/* ─── Section 8: Insurance / Corporate (optional) ─── */}
        <FormSection
          icon={<Shield size={16} />}
          title="Insurance / Corporate"
          description="Coverage and corporate billing details"
          action={<SectionToggle checked={optionalSections.insurance} onChange={() => toggleOptionalSection('insurance')} />}
        >
          {optionalSections.insurance ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div><label className={labelClass}>Insurance Company</label><input className={ic('insuranceCompany')} value={form.insuranceCompany} onChange={(e) => update('insuranceCompany', e.target.value)} placeholder="Insurance company" />{err('insuranceCompany')}</div>
            <div><label className={labelClass}>Policy Number</label><input className={ic('policyNumber')} value={form.policyNumber} onChange={(e) => update('policyNumber', e.target.value)} placeholder="Policy number" />{err('policyNumber')}</div>
            <div><label className={labelClass}>Corporate Organization</label><input className={ic('corporateOrg')} value={form.corporateOrg} onChange={(e) => update('corporateOrg', e.target.value)} placeholder="Corporate organization" />{err('corporateOrg')}</div>
            <div><label className={labelClass}>Member ID</label><input className={ic('memberId')} value={form.memberId} onChange={(e) => update('memberId', e.target.value)} placeholder="Member ID" />{err('memberId')}</div>
            <div><label className={labelClass}>Validity</label><DatePicker className={ic('insuranceValidity')} value={form.insuranceValidity} onChange={(value) => update('insuranceValidity', value)} />{err('insuranceValidity')}</div>
            <div><label className={labelClass}>Coverage %</label><ValidatedNumberInput max={100} className={ic('coveragePercent')} value={form.coveragePercent ?? ''} onChange={(v) => update('coveragePercent', v || undefined)} placeholder="0-100" />{err('coveragePercent')}</div>
            <div className="sm:col-span-2"><label className={labelClass}>Authorization Number</label><input className={ic('authorizationNumber')} value={form.authorizationNumber} onChange={(e) => update('authorizationNumber', e.target.value)} placeholder="Authorization number" />{err('authorizationNumber')}</div>
          </div>
          ) : (
            <CollapsedHint label="Insurance / corporate details" />
          )}
        </FormSection>

        {/* ─── Section 9: Clinical Information (optional) ─── */}
        <FormSection
          icon={<HeartPulse size={16} />}
          title="Clinical Information"
          description="Clinical alerts and medical notes"
          action={<SectionToggle checked={optionalSections.clinical} onChange={() => toggleOptionalSection('clinical')} />}
        >
          {!optionalSections.clinical ? (
            <CollapsedHint label="Clinical information" />
          ) : (
            <>
              {dynamicAlerts.length === 0 ? (
                <p className="text-[0.6875rem] text-text-muted">
                  No clinical alerts configured. Add alerts under Patients &rarr; Clinical Alerts.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {dynamicAlerts.map((alert) => {
                    const active = selectedAlertIds.includes(alert.id);
                    return (
                      <label
                        key={alert.id}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[0.6875rem] font-medium transition-colors ${
                          active
                            ? 'border-warning/40 bg-warning/10 text-warning'
                            : 'border-border bg-background/60 text-text-muted hover:border-warning/30'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className={checkboxClass}
                          checked={active}
                          onChange={() => toggleClinicalAlert(alert.id)}
                        />
                        {alert.label}
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="my-4 border-t border-border/70" />

              <label className={labelClass}>Medical Note</label>
              <textarea
                className={`${ic('medicalNote')} h-auto py-2`}
                value={form.medicalNote}
                onChange={(e) => update('medicalNote', e.target.value)}
                placeholder="Allergies, chronic conditions, ongoing medication…"
                rows={3}
              />
              {err('medicalNote')}
            </>
          )}
        </FormSection>
        </div>
        {/* ═══ end main column ═══ */}

        {/* ═══ Sidebar ═══ */}
        <aside className="space-y-4">
          {/* Patient photo */}
          <FormSection
            icon={<ImageIcon size={16} />}
            title="Patient Photo"
            description="Shown on the patient profile"
          >
            <div className="mx-auto w-[168px]">
              <label className={labelClass}>Photo</label>
              <div
                onDrop={handlePhotoDrop}
                onDragOver={handlePhotoDragOver}
                onDragLeave={handlePhotoDragLeave}
                onClick={() => photoInputRef.current?.click()}
                className={`relative h-[200px] w-full cursor-pointer overflow-hidden rounded-xl border-2 border-dashed transition-colors ${
                  isPhotoDragOver
                    ? 'border-primary bg-primary/5'
                    : photoPreview
                      ? 'border-success bg-success/5'
                      : 'border-border bg-background hover:border-primary/50 hover:bg-primary/5'
                }`}
              >
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoSelect}
                />
                {photoPreview ? (
                  <>
                    <img
                      src={photoPreview}
                      alt="Patient preview"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); clearPhoto(); }}
                      className="absolute right-2 top-2 z-10 rounded-lg bg-danger p-1.5 text-white shadow-sm transition-colors hover:bg-danger-dark"
                      title="Remove photo"
                    >
                      <Trash2 size={14} />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <p className="text-center text-xs font-medium text-white">Click or drop to replace</p>
                    </div>
                  </>
                ) : (
                  <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-text-muted">
                    <div className={`rounded-full p-3 transition-colors ${isPhotoDragOver ? 'bg-primary/10' : 'bg-background'}`}>
                      {isPhotoDragOver ? <Upload size={22} className="text-primary" /> : <ImageIcon size={22} />}
                    </div>
                    <p className="text-center text-[0.6875rem] font-medium">
                      {isPhotoDragOver ? 'Drop image here' : 'Drag & drop or click'}
                    </p>
                    <p className="text-center text-[0.625rem] opacity-70">JPG, PNG, WEBP up to 5MB</p>
                  </div>
                )}
              </div>
              {err('photoUrl')}
            </div>
          </FormSection>

          {/* Registration details */}
          <FormSection
            icon={<ClipboardList size={16} />}
            title="Registration"
            description="Patient category for billing and reporting"
          >
            <FormField
              label="Patient Category"
              hint={selectedCategory?.description || undefined}
            >
              <select className={ic('patientCategory')} value={form.patientCategory} onChange={(e) => update('patientCategory', e.target.value)}>
                {categoryOptions.map((pc) => <option key={pc.value} value={pc.value}>{pc.label}</option>)}
              </select>
            </FormField>
            {err('patientCategory')}
            <Link
              to="/patients/categories"
              className="mt-2 inline-flex items-center gap-1 text-[0.6875rem] font-medium text-primary hover:underline"
            >
              Manage categories
              <ArrowUpRight size={12} />
            </Link>
          </FormSection>

          {/* Account status (edit only) */}
          {isEdit && (
            <FormSection
              icon={<ToggleRight size={16} />}
              title="Account Status"
              description="Visibility in the patient list"
            >
              <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background/60 p-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                    }`}
                  >
                    {isActive ? <ToggleRight size={17} /> : <ToggleLeft size={17} />}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text">{isActive ? 'Active' : 'Inactive'}</p>
                    <p className="text-[0.6875rem] leading-snug text-text-muted">
                      {isActive ? 'Appears in patient searches and pickers.' : 'Hidden from patient searches and pickers.'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isActive}
                  onClick={() => setIsActive((prev) => !prev)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                    isActive ? 'bg-success' : 'bg-border'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                      isActive ? 'translate-x-[24px]' : 'translate-x-[4px]'
                    }`}
                  />
                </button>
              </div>
            </FormSection>
          )}
        </aside>

        {/* ─── Sticky footer actions ─── */}
        <div className="sticky bottom-0 z-20 flex items-center justify-between gap-3 rounded-xl border border-border bg-surface/95 px-3 py-2.5 shadow-lg backdrop-blur xl:col-span-2">
          <p className="hidden text-xs text-text-muted sm:block">
            Fields marked <span className="font-semibold text-danger">*</span> are required.
          </p>
          <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
            <button
              type="button"
              onClick={goBack}
              className="h-9 rounded-lg border border-border bg-surface px-3.5 text-sm font-medium text-text-muted transition-colors hover:bg-background hover:text-text"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={(isEdit ? updatePatient.isPending : createPatient.isPending) || isUploading}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
            >
              {((isEdit ? updatePatient.isPending : createPatient.isPending) || isUploading) && (
                <Loader2 size={15} className="animate-spin" />
              )}
              {isEdit ? <CheckCircle size={15} /> : <UserPlus size={15} />}
              {isUploading
                ? 'Uploading…'
                : isEdit
                  ? 'Save Changes'
                  : 'Register Patient'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
